import { useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";

/* ---------------- TEMPLATE CONFIG ---------------- */

const TEMPLATES = [
  {
    id: "T1",
    name: "Template 1 – Offer Range",
    imageUrl:
      "https://res.cloudinary.com/dztz5ltuq/image/upload/v1767394132/ChatGPT_Image_Jan_3_2026_02_20_or547i.png",
  },
  {
    id: "T2",
    name: "Template 2 – Price Update",
    imageUrl:
      "https://res.cloudinary.com/dztz5ltuq/image/upload/v1767394145/ChatGPT_Image_Jan_3_2026_02_23_zqyqhw.png",
  },
  {
    id: "T3",
    name: "Template 3 – Goodbye",
    imageUrl:
      "https://res.cloudinary.com/dztz5ltuq/image/upload/v1767394132/ChatGPT_Image_Jan_3_2026_02_20_or547i.png",
  },
  {
    id: "T4",
    name: "Template 4 – Best Deal",
    imageUrl:
      "https://res.cloudinary.com/dztz5ltuq/image/upload/v1767394132/ChatGPT_Image_Jan_3_2026_02_20_or547i.png",
  },
];

export default function SendTemplate() {
  const { state } = useLocation();
  const { leadId } = useParams();

  const {
    model: initialModel = "",
    minPrice: initialMin = "",
    maxPrice: initialMax = "",
    mobileNumber,
  } = state || {};

  /* ---------------- STATE ---------------- */

  const [model, setModel] = useState(initialModel);
  const [minPrice, setMinPrice] = useState(initialMin);
  const [maxPrice, setMaxPrice] = useState(initialMax);
  const [greetingType, setGreetingType] = useState("HI");

  const [sendingId, setSendingId] = useState(null); // 🔄 loading
  const [alert, setAlert] = useState(null); // success | error

  /* ---------------- DERIVED VALUES ---------------- */

  const title = useMemo(() => {
    if (!model) return "";
    return model.trim().split(" ")[0];
  }, [model]);

  const greeting =
    greetingType === "HI_MODEL" ? `Hi ${title}` : "Hi Offline";

  const priceRange = `₹${minPrice}–₹${maxPrice}`;

  /* ---------------- TEMPLATE TEXT ---------------- */

  const getTemplateText = (templateId) => {
    switch (templateId) {
      case "T1":
        return `${greeting},

Sell your ${model} with *TRUST N RIDE* 🚗
Get instant payment up to *${priceRange}*.

✨ Why choose us?
✅ Free car evaluation at your doorstep
✅ Instant payment
✅ RC transfer done

👉 Tap Sell Now 💰
📞 Call us today`;

      case "T2":
        return `${greeting},

Price Update from *TRUST N RIDE* 🚗
Your ${model} is ready for instant payment.

💰 New offer amount: *${priceRange}*`;

      case "T3":
        return `${greeting},

It’s time to say goodbye to your ${model} 🚗
*TRUST N RIDE* gives you instant payment of *${priceRange}*.`;

      case "T4":
        return `${greeting},

Wanted to sell your ${model}?
Get the best deal today 💰

💸 Instant payment of *${priceRange}* with *TRUST N RIDE*.`;

      default:
        return "";
    }
  };

  /* ---------------- SEND API ---------------- */

 const sendTemplate = async (templateId) => {
  try {
    // 🔴 FRONTEND VALIDATION
    if (
      String(minPrice).length < 6 ||
      String(maxPrice).length < 6
    ) {
      setAlert({
        type: "error",
        message: "Amount not in lakhs",
      });
      return;
    }

    setSendingId(templateId);
    setAlert(null);

    const res = await fetch(
      "https://trustnride-backend-production-d718.up.railway.app/api/lead/send/template",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leadId,
          templateId,
          title,
          model,
          minPrice,
          maxPrice,
          mobileNumber,
        }),
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to send");
    }

    window.alert("Message sent successfully ✅");

    setAlert({
      type: "success",
      message: "Message sent successfully ✅",
    });
  } catch (err) {
    setAlert({
      type: "error",
      message: err.message || "Send failed ❌",
    });
  } finally {
    setSendingId(null);
  }
};


  /* ---------------- UI ---------------- */

  return (
    <div>
      <Navbar />
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl font-semibold mb-4">
            Send WhatsApp Message
          </h2>

          {/* ALERT */}
          {alert && (
            <div
              className={`mb-4 p-3 rounded text-sm ${
                alert.type === "success"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {alert.message}
            </div>
          )}

          {/* CONTROLS */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <select
              value={greetingType}
              onChange={(e) => setGreetingType(e.target.value)}
              className="border p-2 rounded"
            >
              <option value="HI">Hi Offline</option>
              <option value="HI_MODEL">Hi {title}</option>
            </select>

            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="border p-2 rounded"
              placeholder="Model"
            />

            <input
              type="number"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              className="border p-2 rounded"
              placeholder="Min price"
            />

            <input
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              className="border p-2 rounded"
              placeholder="Max price"
            />
          </div>

          {/* TEMPLATE CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TEMPLATES.map((t) => (
              <div
                key={t.id}
                className="bg-white border rounded-lg shadow overflow-hidden"
              >
                <img
                  src={t.imageUrl}
                  alt={t.name}
                  className="w-full h-[190px] object-contain bg-gray-100"
                />

                <div className="p-4">
                  <h3 className="font-semibold mb-2">{t.name}</h3>

                  <pre className="text-sm whitespace-pre-wrap mb-4">
                    {getTemplateText(t.id)}
                  </pre>

                  <button
                    disabled={sendingId === t.id}
                    onClick={() => sendTemplate(t.id)}
                    className={`px-4 py-2 rounded text-white ${
                      sendingId === t.id
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700"
                    }`}
                  >
                    {sendingId === t.id ? "Sending…" : "Send"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
